import { useEffect, useState } from "react";

export function HealthScoreHalo({ score = 0, size = 280, label = "HEALTH SCORE" }) {
    const [displayed, setDisplayed] = useState(0);
    const r = size / 2 - 18;
    const c = 2 * Math.PI * r;
    const offset = c - (displayed / 100) * c;

    useEffect(() => {
        let start = 0;
        const target = score;
        const duration = 1400;
        const startedAt = performance.now();
        let raf;
        const step = (now) => {
            const t = Math.min(1, (now - startedAt) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplayed(Math.round(start + (target - start) * eased));
            if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [score]);

    const status =
        score >= 85 ? { label: "Excellent", color: "var(--medx-orange)" } :
        score >= 70 ? { label: "Good", color: "var(--medx-orange-vivid)" } :
        score >= 50 ? { label: "Fair", color: "var(--medx-red)" } :
        score > 0 ? { label: "Needs attention", color: "var(--medx-red-deep)" } :
        { label: "Awaiting kiosk scan", color: "#9ca3af" };

    return (
        <div
            className="relative inline-block"
            style={{ width: size, height: size }}
            data-testid="health-score-halo"
        >
            <div className="halo-ring" />
            <div className="halo-pulse-1" />
            <div className="halo-pulse-2" />
            <div className="halo-pulse-3" />
            <div className="absolute inset-2 rounded-full bg-white shadow-[0_20px_60px_-20px_rgba(230,57,70,0.5)] grid place-items-center z-10">
                <svg width={size - 16} height={size - 16} className="-rotate-90">
                    <defs>
                        <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#e63946" />
                            <stop offset="100%" stopColor="#f77f00" />
                        </linearGradient>
                    </defs>
                    <circle
                        cx={(size - 16) / 2}
                        cy={(size - 16) / 2}
                        r={r}
                        stroke="#f3f4f6"
                        strokeWidth="14"
                        fill="none"
                    />
                    <circle
                        cx={(size - 16) / 2}
                        cy={(size - 16) / 2}
                        r={r}
                        stroke="url(#scoreGrad)"
                        strokeWidth="14"
                        strokeLinecap="round"
                        fill="none"
                        strokeDasharray={c}
                        strokeDashoffset={offset}
                        style={{ transition: "stroke-dashoffset 0.4s ease-out" }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="text-[10px] font-mono tracking-[0.22em] text-gray-500">{label}</div>
                    <div className="font-display font-bold text-gray-900 leading-none mt-1" style={{ fontSize: size * 0.28 }} data-testid="health-score-value">
                        {displayed}
                    </div>
                    <div className="text-xs font-medium mt-1" style={{ color: status.color }} data-testid="health-score-status">
                        {status.label}
                    </div>
                </div>
            </div>
        </div>
    );
}
